import { Badge } from '@aod/ui/components/ui/badge';
import { Button } from '@aod/ui/components/ui/button';
import { Progress } from '@aod/ui/components/ui/progress';
import { Separator } from '@aod/ui/components/ui/separator';
import { Hammer, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type Translate, useT } from '../../i18n/index.js';
import { buildingLabel, isBuildAlreadyQueued, isSingleInstanceBuilding } from './buildingGuards.js';

export interface BuildingDef {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly cost: number;
  readonly buildTime: number;
  readonly requiredBuilding?: string;
  readonly requiredResource?: string;
  readonly minCityTier?: number;
  readonly maxPerCity?: number;
}

export interface BuildQueueItem {
  buildingId: string;
  isUnit: boolean;
  progressTicks: number;
  totalTicks: number;
  progressPct: number;
}

interface Props {
  cityId: string;
  cityTier: number;
  buildings: ReadonlyArray<BuildingDef>;
  completedBuildings: ReadonlyArray<string>;
  buildingProgress: string | undefined;
  progressPct: number;
  treasuryIndustry: number;
  buildQueue?: ReadonlyArray<BuildQueueItem>;
  onStartBuilding: (cityId: string, buildingId: string) => void;
}

function buildingBlockedReason(
  t: Translate,
  b: BuildingDef,
  defs: ReadonlyArray<BuildingDef>,
  completedBuildings: ReadonlyArray<string>,
  cityTier: number,
  treasuryIndustry: number,
): string | null {
  if (b.minCityTier && b.minCityTier > 0 && cityTier < b.minCityTier) {
    return t('building.requiresTier', { tier: b.minCityTier });
  }
  if (b.requiredBuilding && !completedBuildings.includes(b.requiredBuilding)) {
    return t('building.requires', { building: buildingLabel(t, b.requiredBuilding, defs) });
  }
  if (b.maxPerCity && b.maxPerCity > 0) {
    const count = completedBuildings.filter((id) => id === b.id).length;
    if (count >= b.maxPerCity) return t('building.alreadyBuilt');
  }
  if (treasuryIndustry < b.cost) return t('building.tooExpensive');
  return null;
}

export function BuildingSection({
  cityId,
  cityTier,
  buildings,
  completedBuildings,
  buildingProgress,
  progressPct,
  treasuryIndustry,
  buildQueue,
  onStartBuilding,
}: Props) {
  const t = useT();
  // Single-instance buildings (Dragon Roost): lock the button from the click until the
  // snapshot shows the project, so a double click cannot queue (and pay for) it twice.
  const [justStarted, setJustStarted] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (justStarted.size === 0) return;
    const timer = setTimeout(() => setJustStarted(new Set()), 1500);
    return () => clearTimeout(timer);
  }, [justStarted]);
  return (
    <>
      <Separator />
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-medium text-muted-foreground">{t('building.title')}</span>
        {buildingProgress && (
          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground">
              {t('building.inProgress', {
                name: buildingLabel(t, buildingProgress, buildings),
                pct: progressPct,
              })}
            </span>
            <Progress value={progressPct} aria-label={t('building.progress')} />
          </div>
        )}
        {buildQueue && buildQueue.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              {t('building.queue', { count: buildQueue.length })}
            </span>
            {buildQueue.map((item, i) => (
              <div key={`${item.buildingId}-${i}`} className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-muted-foreground">
                    {item.isUnit
                      ? t('building.recruiting', {
                          id: buildingLabel(t, item.buildingId, buildings),
                        })
                      : t('building.building', {
                          id: buildingLabel(t, item.buildingId, buildings),
                        })}
                  </span>
                  <span className="tabular-nums text-muted-foreground/70">{item.progressPct}%</span>
                </div>
                <Progress
                  value={item.progressPct}
                  aria-label={t('building.itemProgress', {
                    id: buildingLabel(t, item.buildingId, buildings),
                  })}
                  className="h-1.5"
                />
              </div>
            ))}
          </div>
        )}
        {completedBuildings.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {completedBuildings.map((id) => (
              <Badge key={id} variant="outline" className="text-[10px]">
                {buildingLabel(t, id, buildings)}
              </Badge>
            ))}
          </div>
        )}
        {!buildingProgress &&
          (() => {
            const available = buildings.filter((b) => !completedBuildings.includes(b.id));
            if (available.length === 0) return null;
            return (
              <div className="max-h-[140px] overflow-y-auto space-y-1 pr-0.5">
                {available.map((b) => {
                  const blocked = buildingBlockedReason(
                    t,
                    b,
                    buildings,
                    completedBuildings,
                    cityTier,
                    treasuryIndustry,
                  );
                  const queued =
                    isBuildAlreadyQueued(b.id, buildingProgress, buildQueue) ||
                    justStarted.has(b.id);
                  const reason = queued ? t('building.alreadyQueued') : blocked;
                  const canAfford = reason === null;
                  return (
                    <Button
                      key={b.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2 text-[11px]"
                      disabled={!canAfford}
                      onClick={() => {
                        if (isSingleInstanceBuilding(b.id)) {
                          setJustStarted((s) => new Set(s).add(b.id));
                        }
                        onStartBuilding(cityId, b.id);
                      }}
                      aria-label={t('building.build', { name: b.name })}
                      title={reason ?? undefined}
                    >
                      {canAfford ? (
                        <Hammer className="size-3" aria-hidden />
                      ) : (
                        <Lock className="size-3 text-muted-foreground/60" aria-hidden />
                      )}
                      <span
                        className={`flex-1 truncate ${!canAfford ? 'text-muted-foreground/60' : ''}`}
                      >
                        {b.name}
                      </span>
                      {reason ? (
                        <span className="text-[9px] text-muted-foreground/60 tabular-nums">
                          {reason}
                        </span>
                      ) : (
                        <span className="tabular-nums text-muted-foreground">
                          {t('building.cost', { cost: b.cost })}
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>
            );
          })()}
      </div>
    </>
  );
}
